import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import ClientesTable from "../components/ClientesTable";
import { useGlobalContext } from "../Config";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import FormCrearCliente from "../components/FormCrearCliente.js";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import Loader from "../components/Loader.js";

const Clientes = () => {
  const { baseUrl } = useGlobalContext();
  const location = useLocation();
  const toast = useRef();

  // --------- ESTADOS ---------
  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState(null);
  const [clientes, setClientes] = useState("");
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isFormVisibleCotizacion, setIsFormVisibleCotizacion] = useState(
    location.state?.isFormVisibleCotizacion || false
  );

  // --------- FUNCIONES DE FILTRADO ---------
  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      nombre_producto: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
      },
    });
    setGlobalFilterValue("");
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <>
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
          <span className="p-input-icon-left" style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
          <i className="pi pi-search" />
            <InputText
              value={globalFilterValue ? globalFilterValue.toUpperCase() : ""}
              onChange={(e) =>
                onGlobalFilterChange({ target: { value: e.target.value.toUpperCase() } })
              }
              placeholder="Buscar por Razón Social"
            />
          </span>
          <span className="p-input-icon-left" style={{display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
            {clientes?.length} clientes
          </span>
        </div>
      </>
    );
  };

  // --------- FUNCIONES DE DATOS ---------
  const fetchData = async (url, options = {}) => {
    const { parseJson = true, ...fetchOptions } = options;
  
    const requestOptions = {
      method: "GET",
      headers: new Headers(),
      redirect: "follow",
      credentials: "include",
      ...fetchOptions,
    };
  
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(`Network response was not ok. Status: ${response.status}`);
    }
  
    return parseJson ? response.json() : response;
  };

  const searchCliente = async () => {
    try {
      setLoading(true);
      const url = `${baseUrl}getClientesByFiltro?rut=${""}&razonSocial=${""}&nombreFantasia=${""}&alias=${""}`;
      const data = await fetchData(url);
      if (Array.isArray(data.clientes)) {
        setClientes(data.clientes);
      } else {
        console.error("Data.clientes no es array:", data.clientes);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  // --------- FUNCIONES DE GESTIÓN ---------
  const handleEditCliente = (clienteSeleccionado) => {
    if (!clienteSeleccionado) {
      console.error("Cliente no recibido");
      return;
    }
    setCliente(clienteSeleccionado);
    setIsFormVisible(true);
  };

  const toggleFormVisibility = () => {
    if (isFormVisible || isFormVisibleCotizacion) {
      setIsFormVisible(false);
      setIsFormVisibleCotizacion(false);
      const newState = location.state
        ? { ...location.state, isFormVisibleCotizacion: false }
        : { isFormVisibleCotizacion: false };
      window.history.replaceState(newState, "");
    } else {
      setCliente(null);
      setIsFormVisible(true);
    }
  };

  const handleCambiarEstado = async (cliente) => {
    if (cliente.activo && !window.confirm("¿Está seguro que desea desactivar este cliente?")) {
      return;
    }

    if (!cliente.activo && !window.confirm("¿Está seguro que desea activar este cliente?")) {
      return;
    }

    const nuevoEstado = cliente.activo ? 0 : 1;
    try {
      const url = `${baseUrl}cambiarEstadoCliente?idCliente=${cliente.id}&activo=${nuevoEstado}`;
      const myHeaders = new Headers();
      myHeaders.append("Authorization", "Basic YWRtaW46a2VzcA==");
      myHeaders.append("Content-Type", "application/json;charset=UTF-8");
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
      };
      const response = await fetch(url, requestOptions);
  
      if (!response.ok) {
        throw new Error("Error al cambiar el estado del cliente");
      }
  
      toast.current.show({
        severity: "success",
        summary: "Operación Exitosa",
        detail: `El cliente ha sido ${nuevoEstado === 1 ? "activado" : "desactivado"} correctamente.`,
        life: 3000,
      });

      searchCliente();
    } catch (error) {
      console.error("Error:", error);
      toast.current.show({
        severity: "error",
        summary: "Error en la Operación",
        detail: "No se pudo modificar el estado del cliente. Por favor, intente nuevamente.",
        life: 3000,
      });
    }
  };

  // --------- EFFECTS ---------
  useEffect(() => {
    searchCliente();
    initFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.idCliente) {
      const obtenerClientePorId = async () => {
        try {
          const response = await fetch(`${baseUrl}getClienteById?idCliente=${location.state.idCliente}`);
          const data = await response.json();
          if (data) {
            handleEditCliente(data);
          } else {
            console.error("No se encontró información del cliente");
          }
        } catch (error) {
          console.error("Error al obtener cliente por ID:", error);
        }
      };
      obtenerClientePorId();
    }
  }, [location.state]);

  // --------- RENDER ---------
  return (
    <div className="grid">
        <div className="col-12">
          <div className="card">
          <h4 style={{ marginBottom: "5px" }}>Clientes</h4>
          <h6 style={{ marginTop: "5px" }}>¿Qué clientes están en la plataforma?</h6>
        </div>
      </div>
      
      <Toast ref={toast} />
      {/* Pop-up para el formulario */}
      <Dialog
        visible={isFormVisible || isFormVisibleCotizacion}
        onHide={toggleFormVisibility}
        style={{ width: "85vw" }}
        closable={false}
        modal
        header={cliente !== null ? "Editar Cliente" : "Crear Cliente"}
      >
        <FormCrearCliente
          modoEdicion={cliente !== null}
          clienteData={cliente}

          onSuccess={() => {
            toast.current.show({
              severity: "success",
              summary: "Operación Exitosa",
              detail: "El cliente ha sido guardado correctamente.",
              life: 3000,
            });
            searchCliente();
            toggleFormVisibility();
          }}
          onError={(error) => {
            console.error("Error:", error);
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail: "Ha ocurrido un error al procesar la operación",
              life: 3000,
            });
          }}
          onClose={() => {
            searchCliente();
            toggleFormVisibility();
          }}
          fetchData={fetchData}
        />
      </Dialog>
      <div className="col-12">
        {loading ? (
          <>
            <Loader />
          </>
        ) : (
          <>
            <div className="card">
              {/* Listado clientes */}
              <Button 
                label="Crear Cliente"
                icon="pi pi-plus"
                onClick={toggleFormVisibility}
                className="p-button p-button-rounded mr-2 mb-2"
                raised
              />
              <ClientesTable 
                clientes={clientes}
                loading={loading}
                id="clientesDataTable"
                isMantenedor={true}
                handleEditCliente={handleEditCliente}
                handleCambiarEstado={handleCambiarEstado}
                header={renderHeader()}
                globalFilterFields={["razon_social"]}
                filters={filters}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Clientes;